import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const MobileLocationContext = createContext();

// The 8 directional coverage region keys (excludes the master `covers_all` flag).
// Defined at module scope so the reference is stable across renders.
const REGION_KEYS = [
  'covers_north',
  'covers_east',
  'covers_west',
  'covers_south',
  'covers_north_east',
  'covers_north_west',
  'covers_south_east',
  'covers_south_west',
];

// Pretty labels for display (matches REGION_KEYS order)
const REGION_LABELS = {
  covers_all: 'All Regions',
  covers_north: 'North',
  covers_east: 'East',
  covers_west: 'West',
  covers_south: 'South',
  covers_north_east: 'North East',
  covers_north_west: 'North West',
  covers_south_east: 'South East',
  covers_south_west: 'South West',
};

export const useMobileLocation = () => {
  const context = useContext(MobileLocationContext);
  if (!context) {
    throw new Error('useMobileLocation must be used within a MobileLocationProvider');
  }
  return context;
};

export const MobileLocationProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for mobile location endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Determine the correct base URL based on login type
  const getMobileLocationBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/mobile-locations/api/v1/mobile-locations`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/mobile-locations/api/v1/mobile-locations`;
    } else {
      throw new Error('Invalid login type for mobile location access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access mobile location data');
    }
  }, [loginType]);

  // ------------------------------------------------------------------
  // GET REQUESTS (READ)
  // ------------------------------------------------------------------

  // Get the mobile location coverage record for a business by registry number
  const getMobileLocation = useCallback(async (registryNumber) => {
    validateAccess();

    if (!registryNumber) {
      throw new Error('Business registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getMobileLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch mobile location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getMobileLocationBaseUrl]);

  // Try to fetch, return null on 404 instead of throwing.
  // Useful for "does this business have a mobile config yet?" checks.
  const tryGetMobileLocation = useCallback(async (registryNumber) => {
    try {
      const result = await getMobileLocation(registryNumber);
      return result;
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('not found')) {
        return null;
      }
      throw err;
    }
  }, [getMobileLocation]);

  // ------------------------------------------------------------------
  // POST REQUESTS (CREATE)
  // ------------------------------------------------------------------

  // Create a new mobile location coverage record.
  // Will fail if a record already exists for the registry number — use
  // upsertMobileLocation if you want create-or-update semantics.
  const createMobileLocation = useCallback(async (locationData) => {
    validateAccess();

    if (!locationData.business_registry_number) {
      throw new Error('Business registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getMobileLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(locationData),
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create mobile location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getMobileLocationBaseUrl]);

  // Upsert (create or fully replace) the mobile location coverage record.
  // This is the "save settings" call — the frontend doesn't need to know
  // whether the row already exists.
  const upsertMobileLocation = useCallback(async (registryNumber, locationData) => {
    validateAccess();

    if (!registryNumber) {
      throw new Error('Business registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getMobileLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/upsert`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...locationData,
            business_registry_number: registryNumber,
          }),
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to save mobile location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getMobileLocationBaseUrl]);

  // ------------------------------------------------------------------
  // PUT REQUESTS (PARTIAL UPDATE)
  // ------------------------------------------------------------------

  // Partially update a mobile location coverage record. Only the fields
  // present in updateData are modified.
  const updateMobileLocation = useCallback(async (registryNumber, updateData) => {
    validateAccess();

    if (!registryNumber) {
      throw new Error('Business registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getMobileLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update mobile location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getMobileLocationBaseUrl]);

  // ------------------------------------------------------------------
  // DELETE REQUESTS
  // ------------------------------------------------------------------

  const deleteMobileLocation = useCallback(async (registryNumber) => {
    validateAccess();

    if (!registryNumber) {
      throw new Error('Business registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getMobileLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`,
        {
          method: 'DELETE',
        }
      );

      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete mobile location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getMobileLocationBaseUrl]);

  // ------------------------------------------------------------------
  // CONVENIENCE TOGGLES
  // ------------------------------------------------------------------

  // Toggle the master is_mobile_service flag
  const toggleMobileService = useCallback(async (registryNumber, enabled) => {
    return updateMobileLocation(registryNumber, { is_mobile_service: enabled });
  }, [updateMobileLocation]);

  // Toggle the covers_all flag (the "covers entire island" master switch)
  const toggleCoversAll = useCallback(async (registryNumber, value) => {
    return updateMobileLocation(registryNumber, { covers_all: value });
  }, [updateMobileLocation]);

  // Toggle a single region by key (e.g. 'covers_north')
  const toggleRegion = useCallback(async (registryNumber, regionKey, value) => {
    if (!REGION_KEYS.includes(regionKey) && regionKey !== 'covers_all') {
      throw new Error(`Invalid region key: ${regionKey}`);
    }
    return updateMobileLocation(registryNumber, { [regionKey]: value });
  }, [updateMobileLocation]);

  // Update multiple regions at once. Pass an object like { covers_north: true, covers_south: false }.
  // Unknown keys are silently dropped to keep the payload safe.
  const updateRegions = useCallback(async (registryNumber, regions) => {
    const updateData = {};
    Object.keys(regions).forEach((key) => {
      if (REGION_KEYS.includes(key) || key === 'covers_all' || key === 'is_mobile_service') {
        updateData[key] = regions[key];
      }
    });

    if (Object.keys(updateData).length === 0) {
      throw new Error('No valid region fields to update');
    }

    return updateMobileLocation(registryNumber, updateData);
  }, [updateMobileLocation]);

  // Enable all regions (sets covers_all and every directional flag to true)
  const enableAllRegions = useCallback(async (registryNumber) => {
    const updateData = { covers_all: true };
    REGION_KEYS.forEach((key) => {
      updateData[key] = true;
    });
    return updateMobileLocation(registryNumber, updateData);
  }, [updateMobileLocation]);

  // Disable all regions (clears covers_all and every directional flag)
  const disableAllRegions = useCallback(async (registryNumber) => {
    const updateData = { covers_all: false };
    REGION_KEYS.forEach((key) => {
      updateData[key] = false;
    });
    return updateMobileLocation(registryNumber, updateData);
  }, [updateMobileLocation]);

  // ------------------------------------------------------------------
  // DATA HELPERS (synchronous - operate on data objects)
  // ------------------------------------------------------------------

  // Return an array of human-readable region names that are currently active
  const getActiveCoverageRegions = useCallback((data) => {
    if (!data) return [];
    return REGION_KEYS
      .filter((key) => data[key] === true)
      .map((key) => REGION_LABELS[key]);
  }, []);

  // Check if any coverage is enabled
  const hasAnyCoverage = useCallback((data) => {
    if (!data) return false;
    if (data.covers_all) return true;
    return REGION_KEYS.some((key) => data[key] === true);
  }, []);

  // Count how many of the 8 directional regions are covered
  const getCoverageCount = useCallback((data) => {
    if (!data) return 0;
    if (data.covers_all) return REGION_KEYS.length;
    return REGION_KEYS.filter((key) => data[key] === true).length;
  }, []);

  // Coverage as a percentage (0-100)
  const getCoveragePercentage = useCallback((data) => {
    if (!data) return 0;
    if (data.covers_all) return 100;
    const count = REGION_KEYS.filter((key) => data[key] === true).length;
    return Math.round((count / REGION_KEYS.length) * 100);
  }, []);

  // Format mobile location data for display
  const formatMobileLocationData = useCallback((data) => {
    if (!data) return null;

    const activeRegions = REGION_KEYS
      .filter((key) => data[key] === true)
      .map((key) => REGION_LABELS[key]);

    let coverageSummary;
    if (data.covers_all) {
      coverageSummary = 'Covers all regions';
    } else if (activeRegions.length === 0) {
      coverageSummary = 'No coverage selected';
    } else {
      coverageSummary = `Covers ${activeRegions.length} region${activeRegions.length === 1 ? '' : 's'}: ${activeRegions.join(', ')}`;
    }

    return {
      ...data,
      isMobileService: data.is_mobile_service || false,
      coversAll: data.covers_all || false,
      activeRegions,
      activeRegionCount: activeRegions.length,
      coveragePercentage: data.covers_all ? 100 : Math.round((activeRegions.length / REGION_KEYS.length) * 100),
      coverageSummary,
    };
  }, []);


  // Validate mobile location data. Returns an array of { field, message, severity? }.
  const validateMobileLocationData = useCallback((data) => {
    const errors = [];

    if (!data || typeof data !== 'object') {
      errors.push({ field: 'data', message: 'Mobile location data is required' });
      return errors;
    }

    if (!data.business_registry_number) {
      errors.push({ field: 'business_registry_number', message: 'Business registry number is required' });
    }

    // Boolean type check for any provided fields
    const booleanFields = ['is_mobile_service', 'covers_all', ...REGION_KEYS];
    booleanFields.forEach((field) => {
      if (data[field] !== undefined && typeof data[field] !== 'boolean') {
        errors.push({ field, message: `${field} must be a boolean` });
      }
    });

    // Soft warning: mobile service enabled but no coverage selected
    if (data.is_mobile_service === true && data.covers_all !== true) {
      const hasAny = REGION_KEYS.some((key) => data[key] === true);
      if (!hasAny) {
        errors.push({
          field: 'coverage',
          message: 'Mobile service is enabled but no coverage regions are selected',
          severity: 'warning',
        });
      }
    }

    return errors;
  }, []);

  // Default empty mobile location object — handy for initializing form state
  const getDefaultMobileLocation = useCallback((registryNumber = '') => ({
    business_registry_number: registryNumber,
    is_mobile_service: false,
    covers_all: false,
    covers_north: false,
    covers_east: false,
    covers_west: false,
    covers_south: false,
    covers_north_east: false,
    covers_north_west: false,
    covers_south_east: false,
    covers_south_west: false,
  }), []);

  // ------------------------------------------------------------------
  // INFRASTRUCTURE
  // ------------------------------------------------------------------

  // Health check for mobile location service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master'
        ? `${API_BASE_URL}/master/mobile-locations/health`
        : `${API_BASE_URL}/admin/mobile-locations/health`;

      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master'
        ? `${API_BASE_URL}/master/mobile-locations/api/v1/mobile-locations`
        : `${API_BASE_URL}/admin/mobile-locations/api/v1/mobile-locations`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master'
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,

    // Constants (exposed for UI components that render region grids)
    REGION_KEYS,
    REGION_LABELS,

    // Core actions (Read)
    getMobileLocation,
    tryGetMobileLocation,

    // Core actions (Create)
    createMobileLocation,
    upsertMobileLocation,

    // Core actions (Update)
    updateMobileLocation,
    toggleMobileService,
    toggleCoversAll,
    toggleRegion,
    updateRegions,
    enableAllRegions,
    disableAllRegions,

    // Core actions (Delete)
    deleteMobileLocation,

    // Data helpers (synchronous)
    getActiveCoverageRegions,
    hasAnyCoverage,
    getCoverageCount,
    getCoveragePercentage,
    formatMobileLocationData,
    validateMobileLocationData,
    getDefaultMobileLocation,

    // Infrastructure
    checkHealth,
    getEndpointInfo,
    clearError,
    


    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasMobileLocationAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <MobileLocationContext.Provider value={value}>
      {children}
    </MobileLocationContext.Provider>
  );
};